import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts'

import {
  type Kpi,
  KpiRow,
  spanDays,
  Pagination,
  Thumb,
  metricCell,
  numberStyle,
} from '../components/AssetBits'
import { ChannelDailyTables } from '../components/ChannelDailyTables'
import { formatCount, TipCard } from '../components/ChartTip'
import { InfoDot } from '../components/InfoDot'
import { StoryDailyPanel } from '../components/StoryDailyPanel'
import { ManualMatchControl } from '../components/ManualMatch'
import { SelectionBar, postOpener, selectColumn } from '../components/SendToRawData'
import { postsLink, taskLink } from '../lib/rawFilters'
import { TableToolbar } from '../components/TableToolbar'
import { Hint } from '../components/Hint'
import { Card, Note, Skeleton, Tag } from '../components/ui'
import {
  api,
  type DailyColumn,
  type DailyRow,
  type SavedKpis,
  type StoryRow,
} from '../lib/api'
import { formatDate, formatRange } from '../lib/dates'
import { formatDuration } from '../lib/numbers'
import { type LanguageGroup, precedingRange, type Scope, useFilters } from '../lib/filters'
import { NO_ROWS, searchOnly } from '../lib/tableData'
import { ViewHeader } from '../shell/ViewHeader'

/**
 * **Stories** — `APPLICATION_SPEC.md` §4.2, the client spec's tables 1–7.
 *
 * The rule this page is built on: **ship what is unblocked and leave the rest
 * visibly absent.** A column Snap refuses is rendered as a column with a stated
 * reason, never quietly dropped and never filled with zero — because a client
 * reads "0 screenshots" as a fact about their content rather than a fact about
 * the API, and reads a *missing* column as work we skipped.
 *
 * ### The seven contracted tables, and how three controls produce them
 *
 * The spec's seven cuts differ in exactly three dimensions — content type,
 * language group and window — so they ship as three controls over one set of
 * tables rather than as seven separate components:
 *
 * | Spec table | Content | Group | Window |
 * |---|---|---|---|
 * | 1 · English Public Stories, last 30 days | Public Stories | English | *Last 30 days* chip |
 * | 2 · English Public Stories, lifetime | Public Stories | English | Snap lifetime |
 * | 3 · English Saved Stories, lifetime | Saved Stories | English | Snap lifetime |
 * | 4 · English Channel Daily | Channel Daily | English | the range |
 * | 5 · International Public Stories | Public Stories | International | Snap lifetime |
 * | 6 · International Saved Stories | Saved Stories | International | Snap lifetime |
 * | 7 · International Channel Daily | Channel Daily | International | the range |
 *
 * **Channel Daily is here as well as on `/channels`** because the client's own
 * doc files it under this tab — one shared component, two mount points.
 *
 * ### What Snap withholds, all measured
 * · **Full Title, Author, Classification** — ClickUp; 0 of 349 stories carry a
 *   title from Snap. Until a story matches, **the thumbnail is its identifier**.
 * · **Duration** — not present for stories at all; spotlights only (677/678).
 * · **Screenshots** — refused: *"SCREENSHOTS field is not supported by asset
 *   type STORY"*. It works at channel level, so Channel Daily has it.
 * · **Replies on saved stories** — refused at that asset type (Q-V1), though it
 *   works per public story and per spotlight.
 * · **Viewers over a date range** — `*_UNIQUES` is deduplicated and cannot be
 *   summed across days, so the table shows **Snap's lifetime figure under every
 *   range** and says so (ST-19; Jesse: practically every view lands in the
 *   first 24 hours). The KPI tile still withholds it.
 */

/** The dataset switch, in the client's own vocabulary — their doc says Public
 *  Stories, Saved Stories and Channel Daily, and those names are how they will
 *  check the screen against the spec. Sentence case, because a tab is a label
 *  and not a title. */
const CONTENT_TABS = [
  { key: 'stories', label: 'Public Stories' },
  { key: 'saved', label: 'Saved Stories' },
  { key: 'daily', label: 'Channel Daily' },
] as const

type Window = 'range' | 'lifetime'

type Content = (typeof CONTENT_TABS)[number]['key']

const NOTES: Record<string, string> = {
  // ST-4: short, plain, and no field constants. These are read on hover by
  // someone reading a number, not by someone auditing our API calls — the
  // measured detail behind each one lives in `CLAUDE.md` and in the API modules.
  STORY_VIEWS: 'Times a Story Was Opened.',
  STORY_UNIQUES: 'People, Not Opens. Snap Lifetime Only.',
  AVG_DAILY_VIEWERS:
    "On a Typical Day, How Many People Watched a Story. Each Channel's Daily Unique Viewers, Averaged Over the Range and Added Across Channels, So Someone Who Watched Two Channels That Day Counts Twice. Not the Number of Different People Over the Whole Range: Snapchat Does Not Report That.",
  AVG_EPISODE_VIEWERS:
    "A Typical Episode's Unique Viewers: Each Episode's Snapchat Lifetime Figure, Averaged Over the Episodes Published in This Range.",
  AVG_EPISODE_VIEWERS_LIFETIME:
    "A Typical Episode's Unique Viewers: Each Episode's Snapchat Lifetime Figure, Averaged Over Every Episode in Snapchat's List.",
  CHANNEL_UNIQUE_VIEWERS:
    "Snapchat's Own Lifetime Unique Story Viewers per Channel, Added Across the Selected Channels. Unique Within Each Channel, Not Across Them: Someone Who Follows Two Channels Counts Twice.",
  SAVED_STORY_VIEWS: 'Times a Saved Story Was Opened.',
  SAVED_STORY_UNIQUES: 'People, Not Opens. Snap Lifetime Only.',
  AVG_VIEW_TIME: 'Total View Time ÷ Views.',
  STORY_COUNT: 'Episodes Published in This Range.',
  air_time:
    "How Long the Story Was Set to Stay Up, From the Matched ClickUp Task's Duration Field. " +
    'Blank Where No Task Matched or Duration Is Not Filled In.',
  run_time:
    "From ClickUp: the Matched Episode's Runtime, or TRT Where Runtime Is Empty. " +
    'Blank Where No Episode Matched or ClickUp Has No Value.',
  episode: 'From the Matched ClickUp Task.',
  author: 'From ClickUp. Blank Means No Writer Recorded.',
  classification: 'Read from the Task Name. Unmarked Shows as Unknown.',
}

/**
 * The contracted columns Snap cannot supply, each with the reason it cannot.
 *
 * These render as **real columns full of em dashes**, not as omissions. The
 * client can hold the spec beside the screen and account for all twelve columns,
 * which is the whole point: a missing column reads as unfinished work, and a
 * zero reads as a fact about their content.
 */
/** The Stories search box: "Search Channel, Title, Episode or Author". */
const SEARCHABLE = searchOnly(['channel', 'title', 'episode', 'author'])

/**
 * Air Time's blank says which of two things is missing — the match, or the
 * field on a matched task — because only the second is the client's to fill in.
 */
export function airTimeCell(row: StoryRow) {
  if (row.air_time) return row.air_time
  return (
    <Hint
      label={row.match_status === 'matched' ? 'Not Set in ClickUp' : 'No Matched ClickUp Task'}
      style={{ color: 'var(--text-label)' }}
    >
      —
    </Hint>
  )
}

/** Hours, so 24 Hours < 2 Days < 7 Days; N/A after every window; blank last. */
export function airTimeSortValue(row: StoryRow): number | undefined {
  return row.air_time_hours ?? (row.air_time ? Number.MAX_SAFE_INTEGER : undefined)
}

/**
 * **Columns a table cannot answer are simply not built.**
 *
 * Public Stories omits Duration and Screenshots; Saved Stories omits those plus
 * Air Time and Replies. Snap refuses them for that asset type. Viewers over a
 * date range is Snap's lifetime figure, labelled as such (ST-19).
 *
 * **Nothing on screen says so any more.** A line naming the absences was shipped
 * and then removed the same day (Nilay: *"instead of showing it always"*) —
 * the list goes to the client once, in `docs/CLIENT-ANSWERS-2026-09-11.md` §3,
 * rather than sitting above every table forever.
 */
/** ST-32. Viewers (Range) — the range's daily unique viewers added up. Saved
 *  stories keep the shortfall caveat their lifetime note carries (Q-C9). */
const DAILY_SUM_NOTE: Record<string, string> = {
  STORY_UNIQUES_DAILY_SUM:
    "Daily Unique Viewers Added Up for the Selected Dates. Someone Who Watched on Two Days Counts Twice, So This Runs Slightly Above Snap's Lifetime Figure (About 5% on a Typical Story).",
  SAVED_STORY_UNIQUES_DAILY_SUM:
    "Daily Unique Viewers Added Up for the Selected Dates. Someone Who Watched on Two Days Counts Twice, So This Runs Slightly Above Snap's Lifetime Figure (About 5% on a Typical Story). " +
    'Per-Story Figures Run Short of Channel Totals.',
}

/** ST-19. Viewers under a date range is Snap's lifetime figure, and the column
 *  must say so. Saved stories keep their own shortfall caveat (Q-C9) and do not
 *  share the first-24-hours premise, which is about public stories. */
const LIFETIME_NOTE: Record<string, string> = {
  STORY_UNIQUES:
    'Lifetime Unique Viewers from Snapchat, Not Limited to the Selected Date Range.',
  SAVED_STORY_UNIQUES:
    'Lifetime Unique Viewers from Snapchat, Not Limited to the Selected Date Range. ' +
    'Saved Stories Keep Gaining Viewers for Longer, and Per-Story Figures Run Short of ' +
    'Channel Totals.',
  default: 'From Snapchat Lifetime, Not Limited to the Selected Date Range.',
}

/** The ClickUp columns on saved stories are real but thin — this explains a
 *  blank cell without costing a column. */
const CLICKUP_SAVED_NOTE =
  'From ClickUp, on the Few Saved Stories That Resolve. Several Are Saved on the ' +
  'Same Day, so Most Cannot Be Matched to One Episode.'


/**
 * The scope this page shows when the URL carries none.
 *
 * **English by default** — the client spec's own first four tables are English,
 * and three channels is a readable table where thirteen is a scroll. `all` is
 * one click away and, once chosen, travels in the URL.
 */
const DEFAULT_SCOPE = 'english' as const

/** `all` is not a value the API takes: it means "do not filter by group". */
function groupParam(scope: Scope): LanguageGroup | undefined {
  return scope === 'all' ? undefined : scope
}

export function Stories() {
  const { from, to, channels, group, period, view, setView } = useFilters()

  // Both the dataset and the window come out of the URL, so a shared link
  // reproduces exactly what the sender was looking at (§2.2). An unknown `view`
  // falls back rather than rendering nothing.
  const content = (CONTENT_TABS.some((tab) => tab.key === view) ? view : 'stories') as Content
  // CO-8: Channel Daily answers a Snap lifetime too. It reads
  // `profile_stats_lifetime` — a separate table filled by a separate API call —
  // rather than aggregating the daily rows, which is why the window is offered
  // on every view here rather than being suppressed on this one.
  const lifetime = period === 'lifetime'
  const scope: Scope = group ?? DEFAULT_SCOPE
  const shared = { from, to, channels, group: groupParam(scope) }

  return (
    <div>
      <ViewHeader
        title="Stories"
        // The controls already say which channels and which window, so the
        // status line says only what they cannot: whose day this is.
        status="Snap's Day Is UTC"
        views={CONTENT_TABS}
        activeView={content}
        defaultScope={DEFAULT_SCOPE}
        onView={(next) => setView(next)}
        // Every view here has a lifetime answer. Channel Daily's is Snap's own
        // per-channel totals, which is what the client doc's *(Lifetime)*
        // heading on tables 4 and 7 asks for.
        allowLifetime
      />


      {content === 'stories' && <PublicStories {...shared} window={lifetime ? 'lifetime' : 'range'} />}
      {content === 'saved' && <SavedStories {...shared} window={lifetime ? 'lifetime' : 'range'} />}
      {content === 'daily' && (
        <>
          <ChannelDailyTables
            from={from}
            to={to}
            channels={channels}
            groups={scope === 'all' ? ['english', 'international'] : [scope]}
            window={lifetime ? 'lifetime' : 'range'}
          />
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Public stories
// ---------------------------------------------------------------------------

interface PageQuery {
  from: string
  to: string
  channels: string[]
  group?: LanguageGroup
  window: Window
}

/**
 * Rows with nothing in the window are dropped — **null *and* zero**.
 *
 * The endpoint returns every story asset in scope and sums stats *within* the
 * window, and Snap returns rows for days **before an episode existed**: measured
 * on the 1 Sep pull, **7,300 of 11,809 per-asset story rows predate their own
 * publish date**, and exactly one of those is non-zero. So an episode published
 * in August carries a row, valued zero, for every July day. Left in, a table
 * headed "last 30 days" lists episodes that had not been written yet and pushes
 * the real ones onto page 4.
 *
 * 🔴 **Figures borrowed from Snap's lifetime do not count** (`lifetimeMetrics`).
 * Since ST-19 (25 Sep) Viewers carries the lifetime total under every range, so
 * it is never empty — and every story with lifetime viewers passed this filter,
 * listed under a range it had no views in. Found 7 Oct from a screenshot: a
 * 27 Jul story under 13–27 Aug with Story Views "—" and 576,290 Viewers; 118 of
 * 178 English rows in that range had no views in it.
 */
export function withDataInWindow(rows: StoryRow[], lifetimeMetrics: string[]): StoryRow[] {
  const borrowed = new Set(lifetimeMetrics)
  return rows.filter((row) =>
    Object.entries(row.metrics).some(
      ([metric, value]) => !borrowed.has(metric) && value !== null && value !== 0,
    ),
  )
}

function sum(rows: StoryRow[], metric: string): number {
  return rows.reduce((total, row) => total + (row.metrics[metric] ?? 0), 0)
}

/** Fractional change, or null where a comparison would be meaningless. */
function delta(current: number, previous: number): number | null {
  if (!previous) return null
  return (current - previous) / previous
}

function PublicStories({ from, to, channels, group, window }: PageQuery) {
  const key = [from, to, channels.join(','), group ?? 'all', window]
  const assets = useQuery({
    queryKey: ['story-assets', ...key],
    queryFn: () => api.storyAssets({ from, to, window, channels, group, assetType: 'STORY' }),
  })

  /**
   * The KPI deltas are computed at **channel level**, not from these rows —
   * measured, after the per-asset version produced a **+97,340%** delta that
   * was arithmetically correct and meaningless.
   *
   * A per-asset sum cannot be compared across periods. Snap's asset list is
   * what it lists *today*, so the episodes that were live in the preceding
   * window have largely aged out of it: English story views over 10 Jul – 8 Aug
   * come to **189,922 per asset against 184,038,313 at channel level**, a factor
   * of 969. `profile_stats_daily` is a complete per-day series that does not
   * depend on which assets Snap still returns, so it is the only honest basis
   * for "up or down against last period" — and `CLAUDE.md`'s standing
   * instruction is already **use channel level**.
   */
  const previous = precedingRange(from, to)
  const channelNow = useQuery({
    queryKey: ['story-kpi-channel', from, to, channels.join(','), group ?? 'all'],
    queryFn: () => api.channelDaily({ from, to, channels, group }),
    enabled: window === 'range',
  })
  const channelBefore = useQuery({
    queryKey: [
      'story-kpi-channel-prev',
      previous.from,
      previous.to,
      channels.join(','),
      group ?? 'all',
    ],
    queryFn: () => api.channelDaily({ from: previous.from, to: previous.to, channels, group }),
    enabled: window === 'range',
  })

  // The Viewers tiles (25 Sep). Averages and Snap-deduplicated figures only —
  // Snap cannot say how many *different* people watched over a range.
  const viewersNow = useQuery({
    queryKey: ['story-viewers', window, from, to, channels.join(','), group ?? 'all'],
    queryFn: () => api.viewerKpis({ from, to, window, channels, group }),
  })
  const viewersBefore = useQuery({
    queryKey: ['story-viewers-prev', previous.from, previous.to, channels.join(','), group ?? 'all'],
    queryFn: () =>
      api.viewerKpis({ from: previous.from, to: previous.to, window: 'range', channels, group }),
    enabled: window === 'range',
  })

  const all = assets.data?.rows ?? NO_ROWS
  const rows = useMemo(
    () =>
      window === 'range' ? withDataInWindow(all, assets.data?.lifetime_metrics ?? []) : all,
    [all, assets.data?.lifetime_metrics, window],
  )
  const withheld = assets.data?.withheld ?? []

  const kpis = useMemo<Kpi[]>(() => {
    const channelTotal = (rows: DailyRow[] | undefined, metric: DailyColumn) =>
      (rows ?? []).reduce((total, row) => total + (row.metrics[metric] ?? 0), 0)

    // 🔴 **Equal *coverage*, not just an equal date range.**
    //
    // `precedingRange` returns 30 calendar days before a 30-day window, and its
    // own note warns that comparing unequal windows produces a delta which is
    // mostly an artefact. It guards the range and never checked whether we hold
    // data for it — and our history starts 20 July, so the window before
    // 12 Aug – 10 Sep is 30 days long and only 23 days deep.
    //
    // Measured on production: 220,681,968 over 30 days against 221,410,509 over
    // 23. That is −0.3% on the totals and renders as "no change", while the
    // per-day rate fell from 9.63m to 7.36m — **a 24% drop showing as flat**,
    // because the short window's missing week cancelled it out.
    //
    // So a delta is offered only when both windows hold the same number of days.
    // No delta is a smaller loss than a confident wrong one.
    const daysIn = (rows: DailyRow[] | undefined) =>
      new Set((rows ?? []).map((row) => row.stat_date)).size
    const nowDays = daysIn(channelNow.data)
    const beforeDays = daysIn(channelBefore.data)
    const comparable = window === 'range' && beforeDays > 0 && beforeDays === nowDays
    const deltaLabel = `Against ${formatRange(previous.from, previous.to)}, at Channel Level — Snap Only Lists Recent Episodes`

    const perAssetViews = sum(rows, 'STORY_VIEWS')
    const viewTime = sum(rows, 'VIEW_TIME_MILLIS')
    const avg = perAssetViews ? viewTime / perAssetViews / 1000 : null

    // Channel level for anything compared over time, per the note on the
    // queries above. On the lifetime window there is no range to compare, so
    // the per-asset totals stand on their own.
    const views =
      window === 'lifetime' ? perAssetViews : channelTotal(channelNow.data, 'STORY_VIEWS')
    const shares =
      window === 'lifetime' ? sum(rows, 'SHARES') : channelTotal(channelNow.data, 'SHARES')
    const replies =
      window === 'lifetime' ? sum(rows, 'REPLIES') : channelTotal(channelNow.data, 'REPLIES')

    const published = rows.filter(
      (row) =>
        row.published_at &&
        row.published_at.slice(0, 10) >= from &&
        row.published_at.slice(0, 10) <= to,
    ).length

    return [
      {
        metric: 'STORY_VIEWS',
        value: views,
        withheld: false,
        delta: comparable
          ? delta(views, channelTotal(channelBefore.data, 'STORY_VIEWS'))
          : null,
        deltaLabel,
        previous: comparable ? channelTotal(channelBefore.data, 'STORY_VIEWS') : null,
      },
      // 🔴 **Two Viewers tiles, and neither is a sum of uniques.** The old
      // lifetime tile added every episode's viewers, so someone who watched 20
      // episodes counted 20 times; over a range it was withheld outright.
      // Channel-level VIEWERS is still not a substitute — it spans every
      // content type and ran 49% above STORY_UNIQUES on a measured day (Q-C10).
      ...(window === 'lifetime'
        ? [
            {
              metric: 'CHANNEL_UNIQUE_VIEWERS',
              label: 'Channel Unique Viewers',
              value: viewersNow.data?.channel_unique_viewers ?? null,
              // Withheld only once answered: a figure still loading is not underivable.
              withheld: viewersNow.isSuccess && viewersNow.data.channel_unique_viewers == null,
              delta: null,
            },
            {
              metric: 'AVG_EPISODE_VIEWERS_LIFETIME',
              label: 'Avg Viewers per Episode',
              kind: 'avg' as const,
              value: viewersNow.data?.avg_viewers_per_episode ?? null,
              // Withheld only once answered: a figure still loading is not underivable.
              withheld: viewersNow.isSuccess && viewersNow.data.avg_viewers_per_episode == null,
              delta: null,
            },
          ]
        : [
            {
              metric: 'AVG_DAILY_VIEWERS',
              label: 'Avg Daily Viewers',
              kind: 'avg' as const,
              value: viewersNow.data?.avg_daily_viewers ?? null,
              // Withheld only once answered: a figure still loading is not underivable.
              withheld: viewersNow.isSuccess && viewersNow.data.avg_daily_viewers == null,
              // Same coverage rule as every delta here: equal days, or none.
              delta:
                comparable &&
                viewersNow.data?.avg_daily_viewers != null &&
                viewersBefore.data?.avg_daily_viewers != null &&
                viewersBefore.data.days === viewersNow.data.days
                  ? delta(viewersNow.data.avg_daily_viewers, viewersBefore.data.avg_daily_viewers)
                  : null,
              deltaLabel,
              previous: viewersBefore.data?.avg_daily_viewers ?? null,
            },
            {
              metric: 'AVG_EPISODE_VIEWERS',
              label: 'Avg Viewers per Episode',
              kind: 'avg' as const,
              value: viewersNow.data?.avg_viewers_per_episode ?? null,
              // Withheld only once answered: a figure still loading is not underivable.
              withheld: viewersNow.isSuccess && viewersNow.data.avg_viewers_per_episode == null,
              // No delta: Snap's list has aged most of the preceding window's
              // episodes out, so the comparison would rest on a few survivors.
              delta: null,
            },
          ]),
      {
        metric: 'AVG_VIEW_TIME',
        label: 'Avg View Time',
        kind: 'avg' as const,
        value: avg,
        display: avg === null ? undefined : `${avg.toFixed(1)}s`,
        // Withheld only once the rows have answered — while they load, the tile
        // read "Not Derivable" for a moment (seen 7 Oct), which is a claim.
        withheld: assets.isSuccess && avg === null,
        // No delta: view time exists per asset only, so the same period
        // comparison that broke Story Views would break it here too.
        delta: null,
      },
      {
        metric: 'SHARES',
        value: shares,
        withheld: false,
        delta: comparable ? delta(shares, channelTotal(channelBefore.data, 'SHARES')) : null,
        deltaLabel,
        previous: comparable ? channelTotal(channelBefore.data, 'SHARES') : null,
      },
      {
        metric: 'REPLIES',
        value: replies,
        withheld: false,
        delta: comparable ? delta(replies, channelTotal(channelBefore.data, 'REPLIES')) : null,
        deltaLabel,
        previous: comparable ? channelTotal(channelBefore.data, 'REPLIES') : null,
      },
      {
        metric: 'STORY_COUNT',
        label: window === 'lifetime' ? 'Episodes in Snap’s List' : 'Episodes Published',
        // Null while loading — a "0" there read as no episodes published.
        value: !assets.isSuccess ? null : window === 'lifetime' ? rows.length : published,
        withheld: false,
        // The asset list ages out, so a count of past-window publishes is not
        // comparable either — same reason as the views delta.
        delta: null,
      },
    ]
  }, [assets.isSuccess, channelBefore.data, channelNow.data, from, previous.from, previous.to, rows, to, viewersBefore.data, viewersNow.data, window])

  return (
    <>
      <KpiRow
        kpis={kpis}
        notes={NOTES}
        span={window === 'lifetime' ? 'lifetime' : { days: spanDays(from, to) }}
      />

      <StoryTable
        title={window === 'lifetime' ? 'Public Stories: Snap Lifetime' : 'Public Stories'}
        rows={rows}
        withheld={withheld}
        lifetimeMetrics={assets.data?.lifetime_metrics ?? []}
        dailySumMetrics={assets.data?.daily_sum_metrics ?? []}
        loading={assets.isLoading}
        error={assets.error as Error | null}
        assetType="STORY"
        window={window}
        info={matchSummary(rows)}
      />

      {/* Below the table, deliberately: the table is what the client asked for
          and what they read first, so a chart above it pushes the deliverable
          down the page. */}
      <div className="mt-4">
        <EpisodeScatter rows={rows} loading={assets.isLoading} />
      </div>
    </>
  )
}

/**
 * What the ClickUp join actually delivered, as a sentence for the table's
 * `info` dot.
 *
 * **A string rather than a component — Nilay, 10 Sep.** It was a `Note` above
 * the table; it is now one hover on the table it describes, which is where the
 * blank Author and Episode # cells it explains actually are.
 *
 * **Counted from the rows on screen, never written as a constant.** A static
 * note claiming the columns are populated would go stale the moment the match
 * rate moved — and it *will* move, as the client fills in publish dates.
 *
 * Only the two facts that explain a blank cell. Re-run counts and date-drift
 * detail are on the rows themselves, and the full breakdown is
 * `/connections/matches`.
 */
function matchSummary(rows: StoryRow[]): string | undefined {
  if (rows.length === 0) return undefined
  const count = (status: string) => rows.filter((row) => row.match_status === status).length
  const gaps = [
    count('ambiguous') > 0 ? `${count('ambiguous')} match more than one task` : null,
    count('unmatched') > 0 ? `${count('unmatched')} have none` : null,
  ].filter(Boolean)

  return (
    `Episode Details from ClickUp on ${count('matched')} of ${rows.length} stories.` +
    (gaps.length > 0 ? ` ${gaps.join(', ')}.` : '')
  )
}

function EpisodeScatter({ rows, loading }: { rows: StoryRow[]; loading: boolean }) {
  const points = rows
    .filter((row) => (row.metrics.STORY_VIEWS ?? 0) > 0 && (row.metrics.VIEW_TIME_MILLIS ?? 0) > 0)
    .map((row) => ({
      x: row.metrics.STORY_VIEWS as number,
      // Average view time per view, derived rather than read: the API's own
      // AVG_VIEW_TIME_MILLIS is non-aggregable, so over a window it is not a
      // number we may sum. Total view time ÷ total views is honest.
      y: (row.metrics.VIEW_TIME_MILLIS as number) / (row.metrics.STORY_VIEWS as number) / 1000,
      // Everything the hover needs to name the episode. Without the title an
      // outlier is a fact you cannot act on.
      channel: row.display_label,
      title: row.title,
      episode: row.episode_number,
      published: formatDate(row.published_at),
      rerun: row.classification === 'rerun',
    }))

  return (
    <Card
      title="Reach Against Hold: Views × Average Seconds Watched"
      info="Each Dot Is One Episode: Views Across, Average Seconds Watched Up."
    >
      {loading ? (
        <Skeleton rows={4} />
      ) : points.length === 0 ? (
        <Note>No Stories with Both Views and View Time in This Range.</Note>
      ) : (
        <div style={{ height: '13rem' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 4, right: 12, bottom: 0, left: 8 }}>
              <CartesianGrid stroke="var(--grid-line)" />
              <XAxis
                type="number"
                dataKey="x"
                name="Story Views"
                tick={{ fill: 'var(--axis-label)', fontSize: 11 }}
                stroke="var(--axis-line)"
                tickFormatter={(v: number) =>
                  v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : `${Math.round(v / 1000)}k`
                }
              />
              <YAxis
                type="number"
                dataKey="y"
                name="Avg Seconds"
                tick={{ fill: 'var(--axis-label)', fontSize: 11 }}
                stroke="var(--axis-line)"
                width={48}
                tickFormatter={(v: number) => `${v.toFixed(0)}s`}
              />
              <ZAxis range={[22, 22]} />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                // Recharts' default tooltip renders the coordinates. The
                // question a reader has is which episode this is.
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null
                  const point = payload[0]!.payload as (typeof points)[number]
                  return (
                    <TipCard
                      title={point.title || `${point.channel}, untitled`}
                      subtitle={[
                        point.channel,
                        point.episode ? `episode ${point.episode}` : null,
                        point.published,
                        point.rerun ? 're-run' : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                      rows={[
                        { label: 'Story Views', value: formatCount(point.x) },
                        { label: 'Avg Seconds Watched', value: `${point.y.toFixed(1)}s` },
                      ]}
                    />
                  )
                }}
              />
              {/* A transparent stroke widens the hit target without changing
                  the mark: SVG hit-testing includes the stroke, so a pointer
                  within 8px of a dot finds it. 300 dots at 8px radius are
                  otherwise pinpoint targets — an explicit anti-pattern in the
                  dataviz method, and the reason the spec asks for a
                  nearest-point layer. */}
              <Scatter
                data={points}
                fill="var(--data-1)"
                fillOpacity={0.5}
                stroke="transparent"
                strokeWidth={8}
                isAnimationActive={false}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  )
}

const SAVED_NOTES: Record<string, string> = {
  SAVED_STORY_VIEWS: 'Times a Saved Story Was Opened, from Snap’s Channel Totals.',
  SAVED_AVG_DAILY_VIEWERS: 'People Watching Saved Stories on an Average Day. Not Added Across Days.',
  SAVED_CHANNEL_UNIQUE_VIEWERS: 'Snap’s Own Unique Count per Channel, Added Across Channels.',
  SAVED_AVG_VIEW_TIME: 'Total Saved-Story Watch Time Divided by Saved Story Views.',
  SAVED_STORY_FAVORITES: 'Times a Saved Story Was Favourited.',
  SAVED_STORY_SUBSCRIBES: 'Subscriptions That Came from a Saved Story.',
}

/**
 * ST-24 — six tiles (Nilay, 7 Oct). Four mirror Public Stories; Favorites and
 * Subscribes stand in for the three saved stories cannot have — Shares is
 * per-asset only, Replies is refused per saved story, and Avg Viewers per
 * Episode is per-asset too.
 *
 * Same comparison rule as every row: equal days in both windows, or no change.
 */
export function savedKpiTiles(
  now: SavedKpis | undefined,
  before: SavedKpis | undefined,
  window: Window,
  /** Null while the saved-story rows are still loading. */
  count: number | null,
  previous: { from: string; to: string },
): Kpi[] {
  const total = (k: SavedKpis | undefined, metric: keyof SavedKpis['totals']) =>
    k?.totals[metric] ?? null
  const viewTime = (k: SavedKpis | undefined) => {
    const views = total(k, 'SAVED_STORY_VIEWS')
    const millis = total(k, 'SAVED_STORY_VIEW_TIME_MILLIS')
    return views && millis !== null ? millis / views / 1000 : null
  }
  const comparable =
    window === 'range' && !!now && !!before && before.days > 0 && before.days === now.days
  const deltaLabel = `Against ${formatRange(previous.from, previous.to)}, the Preceding Window of Equal Length`
  const change = (a: number | null, b: number | null) =>
    comparable && a !== null && b !== null && b !== 0
      ? { delta: delta(a, b), deltaLabel, previous: b }
      : { delta: null }
  const flow = (metric: 'SAVED_STORY_VIEWS' | 'SAVED_STORY_FAVORITES' | 'SAVED_STORY_SUBSCRIBES', label: string): Kpi => {
    const value = total(now, metric)
    return { metric, label, value, withheld: now !== undefined && value === null, ...change(value, total(before, metric)) }
  }
  const avgTime = viewTime(now)

  return [
    flow('SAVED_STORY_VIEWS', 'Saved Story Views'),
    window === 'lifetime'
      ? {
          metric: 'SAVED_CHANNEL_UNIQUE_VIEWERS',
          label: 'Channel Unique Viewers',
          value: now?.channel_unique_viewers ?? null,
          withheld: now !== undefined && now.channel_unique_viewers === null,
          delta: null,
        }
      : {
          metric: 'SAVED_AVG_DAILY_VIEWERS',
          label: 'Avg Daily Viewers',
          kind: 'avg',
          value: now?.avg_daily_viewers ?? null,
          withheld: now !== undefined && now.avg_daily_viewers === null,
          ...change(now?.avg_daily_viewers ?? null, before?.avg_daily_viewers ?? null),
        },
    {
      metric: 'SAVED_AVG_VIEW_TIME',
      label: 'Avg View Time',
      kind: 'avg',
      value: avgTime,
      display: avgTime === null ? undefined : `${avgTime.toFixed(1)}s`,
      withheld: avgTime === null,
      ...change(avgTime, viewTime(before)),
      previousDisplay: viewTime(before) === null ? undefined : `${viewTime(before)!.toFixed(1)}s`,
    },
    flow('SAVED_STORY_FAVORITES', 'Favorites'),
    flow('SAVED_STORY_SUBSCRIBES', 'Subscribes'),
    {
      metric: 'SAVED_STORY_COUNT',
      label: window === 'lifetime' ? 'Saved Stories in Snap’s List' : 'Saved Stories with Views',
      value: count,
      withheld: false,
      // The asset list is what Snap lists today, so a past count is not comparable.
      delta: null,
    },
  ]
}

// ---------------------------------------------------------------------------
// Saved stories — shipped with the Q-C9 shortfall on the page
// ---------------------------------------------------------------------------

function SavedStories({ from, to, channels, group, window }: PageQuery) {
  const key = [from, to, channels.join(','), group ?? 'all', window]
  const assets = useQuery({
    queryKey: ['saved-assets', ...key],
    queryFn: () =>
      api.storyAssets({ from, to, window, channels, group, assetType: 'SAVED_STORY' }),
  })

  // ST-24 — the KPI row, at **channel level** for both windows. Per-asset saved
  // figures run 6.6–11.4× short of the channel (Q-C9), so a tile summed from
  // the rows below would be wrong by the same factor.
  const previous = precedingRange(from, to)
  const now = useQuery({
    queryKey: ['saved-kpis', ...key],
    queryFn: () => api.savedKpis({ from, to, window, channels, group }),
  })
  const before = useQuery({
    queryKey: ['saved-kpis-prev', previous.from, previous.to, channels.join(','), group ?? 'all'],
    queryFn: () =>
      api.savedKpis({ from: previous.from, to: previous.to, window: 'range', channels, group }),
    enabled: window === 'range',
  })

  const all = assets.data?.rows ?? []
  const rows =
    window === 'range' ? withDataInWindow(all, assets.data?.lifetime_metrics ?? []) : all

  const kpis = useMemo<Kpi[]>(
    () => savedKpiTiles(now.data, before.data, window, assets.isSuccess ? rows.length : null, previous),
    // `previous` is a fresh object each render; its two dates are what change.
    [assets.isSuccess, before.data, now.data, previous.from, previous.to, rows.length, window],
  )

  return (
    <>
      {now.data && (
        <KpiRow
          kpis={kpis}
          notes={SAVED_NOTES}
          span={window === 'lifetime' ? 'lifetime' : { days: spanDays(from, to) }}
        />
      )}
      <StoryTable
        title={window === 'lifetime' ? 'Saved Stories: Snap Lifetime' : 'Saved Stories'}
        rows={rows}
        withheld={assets.data?.withheld ?? []}
        lifetimeMetrics={assets.data?.lifetime_metrics ?? []}
        dailySumMetrics={assets.data?.daily_sum_metrics ?? []}
        loading={assets.isLoading}
        error={assets.error as Error | null}
        assetType="SAVED_STORY"
        window={window}
      />
    </>
  )
}

/** An Episode # or Author cell: the value, linking to exactly the ClickUp task
 *  the post matched (RD-22). */
function clickupCell(row: StoryRow, value: string | null) {
  if (!value) return <span style={{ color: 'var(--text-label)' }}>—</span>
  if (!row.chosen_task_id) return value
  return (
    <Link
      to={taskLink(row.chosen_task_id, row.published_at, row.task_publish_date)}
      title="Open the ClickUp Task in Raw Data"
      style={{ color: 'inherit', textDecorationColor: 'var(--border)' }}
    >
      {value}
    </Link>
  )
}

export function StoryTable({
  title,
  rows,
  withheld,
  lifetimeMetrics,
  dailySumMetrics,
  loading,
  error,
  assetType,
  window,
  footer,
  info,
}: {
  title: string
  rows: StoryRow[]
  withheld: string[]
  /** Metrics whose figure is Snap's lifetime total under a date range (ST-19). */
  lifetimeMetrics: string[]
  /** Keys holding a uniques metric's daily figures added up over the range (ST-32). */
  dailySumMetrics: string[]
  loading: boolean
  error: Error | null
  assetType: 'STORY' | 'SAVED_STORY'
  /** Decides where a row's Raw Data link lands (RD-21). */
  window: Window
  /** Provenance only. An *explanation* goes in `info` — see `Card`. */
  footer?: string
  info?: string
}) {
  const isStory = assetType === 'STORY'
  // RD-21: a click opens the dataset that answers what this table shows.
  const opener = useMemo(() => postOpener<StoryRow>(assetType, window), [assetType, window])
  const viewsMetric = isStory ? 'STORY_VIEWS' : 'SAVED_STORY_VIEWS'
  const uniquesMetric = isStory ? 'STORY_UNIQUES' : 'SAVED_STORY_UNIQUES'

  const [sorting, setSorting] = useState<SortingState>([{ id: viewsMetric, desc: true }])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [search, setSearch] = useState('')
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  // ST-23: the row whose views-per-day panel is open.
  const [daily, setDaily] = useState<StoryRow | null>(null)

  const columns = useMemo<ColumnDef<StoryRow>[]>(() => {
    const metricColumn = (metric: string, label: string): ColumnDef<StoryRow> => ({
      id: metric,
      header: () => (
        <>
          {label}
          {(NOTES[metric] || DAILY_SUM_NOTE[metric] || withheld.includes(metric) || lifetimeMetrics.includes(metric)) && (
            <InfoDot
              note={
                DAILY_SUM_NOTE[metric]
                  ? DAILY_SUM_NOTE[metric]!
                  : withheld.includes(metric)
                  ? `Not Derivable over a Date Range. ${label} Counts People, Not Views, so Adding the Days Would Count the Same Person Twice. Snap Lifetime Has the Real Figure.`
                  : lifetimeMetrics.includes(metric)
                    ? LIFETIME_NOTE[metric] ?? LIFETIME_NOTE.default!
                    : NOTES[metric]!
              }
            />
          )}
        </>
      ),
      accessorFn: (row: StoryRow) => row.metrics[metric] ?? null,
      cell: (info) => metricCell(info.getValue() as number | null),
      meta: { numeric: true },
    })

    const thumb: ColumnDef<StoryRow> = {
      id: 'thumb',
      header: '',
      cell: ({ row }) => (
        <Thumb
          assetId={row.original.asset_id}
          has={row.original.has_thumbnail}
          alt={row.original.title || `${row.original.display_label} episode`}
        />
      ),
      enableSorting: false,
    }

    const channel: ColumnDef<StoryRow> = {
      id: 'channel',
      header: 'Channel',
      accessorKey: 'display_label',
    }

    const fullTitle: ColumnDef<StoryRow> = {
      id: 'title',
      header: 'Full Title',
      accessorKey: 'title',
      cell: (context) => (
        <>
          {titleCell(context)}
          {/* MT-17: Set Manually / Applying tags for everyone; the picker for Admins. */}
          <ManualMatchControl target={{ ...context.row.original, asset_type: assetType }} />
        </>
      ),
    }
    function titleCell({ row, getValue }: { row: { original: StoryRow }; getValue: () => unknown }) {
        const value = getValue() as string | null
        if (value) {
          const offset = row.original.match_day_offset
          // A ±1 match means NO ClickUp task was dated that day — it assumes
          // the date drifted. That is a weaker claim than an exact-day match
          // and must not read like one, since there is no join key between
          // the two systems to fall back on.
          // RD-22: a matched title *is* the ClickUp task's name, so it opens
          // exactly that task. The ▾ menu still offers the match itself.
          const task = row.original.chosen_task_id
          const title = (
            <Link
              to={
                task
                  ? taskLink(task, row.original.published_at, row.original.task_publish_date)
                  : postsLink([row.original], { kind: assetType, window, today: '' }, 'story_matches')
              }
              title={task ? 'Open the ClickUp Task in Raw Data' : 'Open the ClickUp Match in Raw Data'}
              style={{ color: 'inherit', textDecorationColor: 'var(--border)' }}
            >
              {value}
            </Link>
          )
          if (offset !== null && offset !== 0) {
            return (
              <span>
                {title}
                <InfoDot
                  note={`Matched to a Task Dated ${Math.abs(offset)} day ${offset > 0 ? 'later' : 'earlier'}.`}
                />
              </span>
            )
          }
          return title
        }
        const status = row.original.match_status
        return (
          <span
            style={{ color: 'var(--text-label)' }}
            title={
              status === 'ambiguous'
                ? `${row.original.candidate_count ?? 0} ClickUp Tasks Share This Channel and Date, so We Do Not Guess Between Them`
                : 'No ClickUp Task with a Publish Date Matches This Story'
            }
          >
            —
          </span>
        )
    }

    const published: ColumnDef<StoryRow> = {
      id: 'published',
      header: 'Publish Date',
      accessorKey: 'published_at',
      cell: (info) => formatDate(info.getValue() as string | null),
    }

    // The spec's column order, so a reader can hold the doc beside the screen:
    // Channel · Full Title · Publish Date · Run Time · Duration · Author ·
    // Classification · Story Views · Viewers · Shares · Replies · Screenshots.
    // Air Time sits beside Run Time. Both come from the matched ClickUp task.
    const base: ColumnDef<StoryRow>[] = [selectColumn(opener), thumb, channel, fullTitle, published]

    // ST-17: the video's length, from ClickUp — both story types, since it
    // comes from the matched task rather than from Snap.
    base.push({
      id: 'run_time',
      header: () => (
        <>
          Run Time
          <InfoDot note={NOTES.run_time!} />
        </>
      ),
      accessorKey: 'run_time_seconds',
      cell: (info) => formatDuration(info.getValue() as number | null),
      meta: { numeric: true },
    })

    if (isStory) {
      base.push({
        id: 'air_time',
        header: () => (
          <>
            Air Time
            <InfoDot note={NOTES.air_time!} />
          </>
        ),
        // ST-27 (Jesse, 13 Oct): the posting window chosen in ClickUp, not
        // Snap's end − start. Sorted by hours, so 24 Hours < 2 Days < 7 Days;
        // N/A sorts after every window, and blanks after that.
        accessorFn: airTimeSortValue,
        // Blanks are ~82% of rows; first on an ascending sort would bury every value.
        sortUndefined: 'last',
        cell: (info) => airTimeCell(info.row.original),
        meta: { numeric: true },
      })
    }
    // Air Time, Duration, Replies and Screenshots are never pushed where the
    // asset type cannot answer them — see NOT_APPLICABLE.

    if (isStory) {
      base.push(
        {
          id: 'episode',
          header: () => (
            <>
              Episode #<InfoDot note={NOTES.episode!} />
            </>
          ),
          accessorKey: 'episode_number',
          cell: (info) => clickupCell(info.row.original, info.getValue() as string | null),
        },
        {
          id: 'author',
          header: () => (
            <>
              Author<InfoDot note={NOTES.author!} />
            </>
          ),
          accessorKey: 'author',
          cell: (info) => clickupCell(info.row.original, info.getValue() as string | null),
        },
        {
          id: 'classification',
          header: () => (
            <>
              Classification<InfoDot note={NOTES.classification!} />
            </>
          ),
          accessorKey: 'classification',
          cell: (info) =>
            info.getValue() === 'rerun' ? (
              <Tag tone="neutral">Re-Run</Tag>
            ) : (
              <span
                style={{ color: 'var(--text-label)' }}
                title="No Re-Run Marker on the Task Name"
              >
                —
              </span>
            ),
        },
      )
    } else {
      // **Saved stories carry these too since CU-3.** They resolve far less
      // often — 12 matched against 26 ambiguous — so most cells are still empty,
      // but an empty cell on a matched row and an empty cell on a type that has
      // no source at all are different facts. The same columns, with the
      // per-row reason, say which.
      base.push(
        {
          id: 'episode',
          header: () => (
            <>
              Episode #<InfoDot note={NOTES.episode!} />
            </>
          ),
          accessorKey: 'episode_number',
          cell: (info) => clickupCell(info.row.original, info.getValue() as string | null),
        },
        {
          id: 'author',
          header: () => (
            <>
              Author<InfoDot note={CLICKUP_SAVED_NOTE} />
            </>
          ),
          accessorKey: 'author',
          cell: (info) => clickupCell(info.row.original, info.getValue() as string | null),
        },
        {
          id: 'classification',
          header: () => (
            <>
              Classification<InfoDot note={CLICKUP_SAVED_NOTE} />
            </>
          ),
          accessorKey: 'classification',
          cell: (info) =>
            info.getValue() === 'rerun' ? <Tag tone="neutral">Re-Run</Tag> : (
              <span style={{ color: 'var(--text-label)' }}>—</span>
            ),
        },
      )
    }

    // ST-23: the views figure opens that story's views per day (Jesse, 6 Oct).
    // The other row targets are taken — thumbnail → lightbox, title → ClickUp
    // task, checkbox → Raw Data — and this is the number he wants broken down.
    const views = metricColumn(viewsMetric, 'Story Views')
    views.cell = ({ row, getValue }) => {
      const value = getValue() as number | null
      if (value === null || value === undefined) return metricCell(value)
      return (
        <button
          type="button"
          className="views-open"
          title="Views per Day"
          aria-label={`Views per Day for ${row.original.title || `${row.original.display_label} episode`}`}
          onClick={() => setDaily(row.original)}
        >
          {metricCell(value)}
        </button>
      )
    }
    base.push(views, metricColumn('SHARES', 'Shares'))
    // Viewers wherever a figure exists: the window's own on Snap Lifetime, and
    // Snap's lifetime total under a date range, labelled as such (ST-19).
    // ST-32 (Nilay, 14 Oct): under a range, the range's own figure goes beside
    // it — the daily viewers added up. Lifetime alone set 38,422 viewers beside
    // 866 views in the range, which reads as 44 viewers per view.
    const dailySum = `${uniquesMetric}_DAILY_SUM`
    if (dailySumMetrics.includes(dailySum)) {
      // Blanks last either way: a story with no viewer rows in the range should
      // not head an ascending sort.
      const blanksLast = (column: ColumnDef<StoryRow>, metric: string): ColumnDef<StoryRow> => ({
        ...column,
        accessorFn: (row: StoryRow) => row.metrics[metric] ?? undefined,
        sortUndefined: 'last',
      })
      base.splice(
        base.length - 1,
        0,
        blanksLast(metricColumn(dailySum, 'Viewers (Range)'), dailySum),
        blanksLast(metricColumn(uniquesMetric, 'Viewers (Lifetime)'), uniquesMetric),
      )
    } else if (!withheld.includes(uniquesMetric)) {
      base.splice(base.length - 1, 0, metricColumn(uniquesMetric, 'Viewers'))
    }

    // Replies works per public story (9,016) and is refused per saved story.
    if (isStory) base.push(metricColumn('REPLIES', 'Replies'))

    return base
  }, [dailySumMetrics, isStory, lifetimeMetrics, opener, uniquesMetric, viewsMetric, withheld])

  const table = useReactTable({
    data: rows,
    columns,
    // What the placeholder promises, whatever the first row holds (lib/tableData).
    getColumnCanGlobalFilter: SEARCHABLE,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setSearch,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Bounded rendering — see Pagination's note. 50 rows is ~50 thumbnail
    // requests instead of 382.
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 50 } },
    // ST-21: selection survives paging and sorting because rows are keyed by
    // asset, not by position.
    getRowId: (row) => row.asset_id,
    enableRowSelection: true,
    state: { sorting, columnFilters, globalFilter: search, rowSelection },
    onRowSelectionChange: setRowSelection,
  })

  return (
    <Card
      title={title}
      action={
        <span className="text-xs" style={{ color: 'var(--text-label)' }}>
          {loading ? '' : `${table.getFilteredRowModel().rows.length} rows`}
        </span>
      }
      footer={footer}
      info={info}
    >
      {loading ? (
        <Skeleton rows={5} />
      ) : error ? (
        <Note tone="error">{error.message}</Note>
      ) : rows.length === 0 ? (
        <Note>Nothing in This Selection.</Note>
      ) : (
        <>
          <StoryDailyPanel row={daily} assetType={assetType} onClose={() => setDaily(null)} />
          <SelectionBar table={table} opener={opener} />
          <TableToolbar
            table={table}
            placeholder="Search Channel, Title, Episode or Author"
            facets={
              isStory
                ? [
                    { id: 'channel', label: 'Channel' },
                    { id: 'author', label: 'Author' },
                    {
                      id: 'classification',
                      label: 'Classification',
                      format: (value) => (value === 'rerun' ? 'Re-Run' : value),
                    },
                  ]
                : [{ id: 'channel', label: 'Channel' }]
            }
          />
          {/* No inner vertical scroll. A 30rem box holding hundreds of rows
              captures the wheel, so the page cannot be scrolled past the table
              until the reader has scrolled through every row inside it — which
              reads exactly like the page freezing. Horizontal scroll stays:
              these tables are 15 columns wide and it does not trap the wheel. */}
          <div style={{ overflowX: 'auto' }}>
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <thead>
              {table.getHeaderGroups().map((group) => (
                <tr key={group.id}>
                  {group.headers.map((header) => {
                    const numeric = (header.column.columnDef.meta as { numeric?: boolean })?.numeric
                    const sortable = header.column.getCanSort()
                    return (
                      <th
                        key={header.id}
                        aria-sort={
                          header.column.getIsSorted() === 'asc'
                            ? 'ascending'
                            : header.column.getIsSorted() === 'desc'
                              ? 'descending'
                              : 'none'
                        }
                        style={{
                          position: 'sticky',
                          top: 0,
                          background: 'var(--bg-surface)',
                          color: 'var(--text-label)',
                          borderBottom: '1px solid var(--border)',
                          padding: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {sortable ? (
                          <button
                            type="button"
                            onClick={header.column.getToggleSortingHandler()}
                            className="w-full px-2 py-2 text-xs font-semibold uppercase"
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: 'inherit',
                              font: 'inherit',
                              letterSpacing: '0.06em',
                              textAlign: numeric ? 'right' : 'left',
                              cursor: 'pointer',
                            }}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {header.column.getIsSorted() === 'asc' && ' ▲'}
                            {header.column.getIsSorted() === 'desc' && ' ▼'}
                          </button>
                        ) : (
                          // An unsortable column gets no button: a control that
                          // does nothing is worse than no control, and every
                          // cell in these columns holds the same em dash.
                          <span
                            className="block px-2 py-2 text-xs font-semibold uppercase"
                            style={{
                              letterSpacing: '0.06em',
                              textAlign: numeric ? 'right' : 'left',
                            }}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                        )}
                      </th>
                    )
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const numeric = (cell.column.columnDef.meta as { numeric?: boolean })?.numeric
                    return (
                      <td
                        key={cell.id}
                        className="px-2 py-1"
                        style={{
                          borderBottom: '1px solid var(--border)',
                          color: 'var(--text-primary)',
                          textAlign: numeric ? 'right' : 'left',
                          whiteSpace: 'nowrap',
                          ...(numeric ? numberStyle : {}),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
      {!loading && rows.length > 0 && (
        <Pagination
          page={table.getState().pagination.pageIndex}
          pageCount={table.getPageCount()}
          rowCount={table.getFilteredRowModel().rows.length}
          onChange={(next) => table.setPageIndex(next)}
        />
      )}
    </Card>
  )
}
